import * as THREE from 'three';
import {
  MESH_NAME_PELL,
  PELL_DEFAULT_RADIUS_M,
  PELL_IRON_BAND_CENTER_FRAC,
  PELL_IRON_BAND_COLOR,
  PELL_IRON_BAND_HEIGHT_M,
  PELL_IRON_BAND_METALNESS,
  PELL_IRON_BAND_OVERHANG_M,
  PELL_IRON_BAND_ROUGHNESS,
  PELL_ROUGHNESS,
  PELL_WOOD_FALLBACK_COLOR,
  PELL_ZONE_COLORS,
  PELL_ZONE_FILL_ALPHA,
  PELL_ZONE_LINE_ALPHA,
  PELL_ZONE_LINE_COLOR,
  PELL_ZONE_LINE_WIDTH_PX,
  PELL_ZONE_OVERLAY_CANVAS_H,
  PELL_ZONE_OVERLAY_CANVAS_W,
  PELL_ZONE_OVERLAY_RADIUS_EPS_M,
  type Zone,
} from '../config/constants';
import { getZoneBands, getZoneBoundaries } from '../setup/zoneBands';
import { loadPellWoodMaps } from './woodTexture';

export type PellOptions = {
  userHeightCm: number;
  radiusM?: number;
};

const PART_IRON_BAND = 'pell-iron-band';
const PART_ZONE_OVERLAY = 'pell-zone-overlay';
const RADIAL_SEGS = 24;

/**
 * Pell cylinder, height scales with user height. Origin at floor center.
 * Reads as a real wooden post: a textured (async-upgraded) cylinder with an iron
 * band near the base. Zone feedback (Q6) is a separate canvas overlay — faint
 * boundary lines always, the target band filled on highlight (see `highlightZoneBand`).
 */
export function createPell(opts: PellOptions): THREE.Mesh {
  const heightM = opts.userHeightCm / 100;
  const radius = opts.radiusM ?? PELL_DEFAULT_RADIUS_M;

  const geom = new THREE.CylinderGeometry(radius, radius, heightM, RADIAL_SEGS, 1, false);
  geom.translate(0, heightM / 2, 0);

  // Solid worn-timber fallback; the wood maps swap in async (graceful on 404).
  const mat = new THREE.MeshStandardMaterial({
    color: PELL_WOOD_FALLBACK_COLOR,
    roughness: PELL_ROUGHNESS,
    metalness: 0,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = MESH_NAME_PELL;
  mesh.userData['heightM'] = heightM;
  mesh.userData['radiusM'] = radius;

  mesh.add(createIronBand(radius, heightM));
  mesh.add(createZoneOverlay(radius, heightM));

  void upgradePellWood(mat);
  return mesh;
}

/** Swap the worn-timber fallback color for the real wood maps. Keeps fallback on failure. */
async function upgradePellWood(mat: THREE.MeshStandardMaterial): Promise<void> {
  try {
    const { color, normal } = await loadPellWoodMaps();
    mat.map = color;
    mat.normalMap = normal;
    mat.color.setHex(0xffffff);
    mat.needsUpdate = true;
  } catch (err) {
    console.warn('[pell] wood textures failed, keeping fallback color', err);
  }
}

function createIronBand(radius: number, heightM: number): THREE.Mesh {
  const bandRadius = radius + PELL_IRON_BAND_OVERHANG_M;
  const geom = new THREE.CylinderGeometry(
    bandRadius,
    bandRadius,
    PELL_IRON_BAND_HEIGHT_M,
    RADIAL_SEGS,
    1,
    true,
  );
  geom.translate(0, heightM * PELL_IRON_BAND_CENTER_FRAC, 0);
  const mat = new THREE.MeshStandardMaterial({
    color: PELL_IRON_BAND_COLOR,
    metalness: PELL_IRON_BAND_METALNESS,
    roughness: PELL_IRON_BAND_ROUGHNESS,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = PART_IRON_BAND;
  return mesh;
}

/**
 * Transparent cylinder at radius+eps carrying a canvas. Open-ended (sides only)
 * so it never occludes the post caps. The canvas/texture live in userData so
 * `highlightZoneBand` can redraw it.
 */
function createZoneOverlay(radius: number, heightM: number): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = PELL_ZONE_OVERLAY_CANVAS_W;
  canvas.height = PELL_ZONE_OVERLAY_CANVAS_H;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;

  const overlayRadius = radius + PELL_ZONE_OVERLAY_RADIUS_EPS_M;
  const geom = new THREE.CylinderGeometry(
    overlayRadius,
    overlayRadius,
    heightM,
    RADIAL_SEGS,
    1,
    true,
  );
  geom.translate(0, heightM / 2, 0);

  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = PART_ZONE_OVERLAY;
  mesh.userData['canvas'] = canvas;
  mesh.userData['tex'] = tex;
  drawZoneOverlay(canvas, tex, null);
  return mesh;
}

function hexToCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/**
 * Redraw the zone overlay: faint ink lines at the four internal boundaries, and —
 * when a zone is highlighted — that band filled with its tint. Reads the live
 * (tunable) bands, so settings edits are reflected on the next call.
 *
 * Canvas Y is flipped vs the post (texture flipY): frac 1 (top) → canvas y 0.
 */
function drawZoneOverlay(
  canvas: HTMLCanvasElement,
  tex: THREE.CanvasTexture,
  highlight: Zone | null,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  if (highlight) {
    const [low, high] = getZoneBands()[highlight];
    const yTop = (1 - high) * h;
    const yBot = (1 - low) * h;
    ctx.globalAlpha = PELL_ZONE_FILL_ALPHA;
    ctx.fillStyle = hexToCss(PELL_ZONE_COLORS[highlight]);
    ctx.fillRect(0, yTop, w, yBot - yTop);
  }

  ctx.globalAlpha = PELL_ZONE_LINE_ALPHA;
  ctx.strokeStyle = PELL_ZONE_LINE_COLOR;
  ctx.lineWidth = PELL_ZONE_LINE_WIDTH_PX;
  for (const frac of getZoneBoundaries()) {
    const y = (1 - frac) * h;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }

  ctx.globalAlpha = 1;
  tex.needsUpdate = true;
}

/**
 * Highlight a target zone band: that zone's band fills with its tint over the wood,
 * the others stay bare (just the boundary lines). Pass `null` to clear the fill.
 * Redraws the overlay canvas (and re-reads the tunable bands).
 */
export function highlightZoneBand(pell: THREE.Mesh, target: Zone | null): void {
  const overlay = pell.getObjectByName(PART_ZONE_OVERLAY);
  const canvas = overlay?.userData['canvas'] as HTMLCanvasElement | undefined;
  const tex = overlay?.userData['tex'] as THREE.CanvasTexture | undefined;
  if (!canvas || !tex) return;
  drawZoneOverlay(canvas, tex, target);
}

/**
 * Dispose a pell mesh and its children (iron band + zone overlay incl. its canvas
 * texture). The shared wood maps are intentionally left alive — they back every
 * rebuild (see woodTexture.ts).
 */
export function disposePell(pell: THREE.Mesh): void {
  pell.geometry.dispose();
  (pell.material as THREE.Material).dispose();
  const iron = pell.getObjectByName(PART_IRON_BAND) as THREE.Mesh | undefined;
  if (iron) {
    iron.geometry.dispose();
    (iron.material as THREE.Material).dispose();
  }
  const overlay = pell.getObjectByName(PART_ZONE_OVERLAY) as THREE.Mesh | undefined;
  if (overlay) {
    overlay.geometry.dispose();
    const mat = overlay.material as THREE.MeshBasicMaterial;
    mat.map?.dispose();
    mat.dispose();
  }
}
